import React, {useState} from 'react';
import {Link, LinkAPIData} from "types/Link";
import {FormikHelpers} from "formik";
import {DateTime} from "luxon";
import IconName from "types/IconName";
import {TagIcon} from "@heroicons/react/solid";
import Form, {FormBlock, FormSection, SelectField, TextField} from "components/Form/Form";
import {Option, ToggleSwitch} from "../../../components/Form/Form/Control";
import {linkUrlRegex, toTitleCase} from "../../../services/string";
import {LinkUrlFieldSet, SiteAttachmentFieldSet} from "../../../components/Form/Form/Fieldset";
import {useLinkOptions} from "../../../hooks/useLinkOptions";
import * as Yup from "yup";
import {useOperator} from "../../../context/OperatorContext";


// Types ----

type LinkFormProps = {
  link?: Link | null
  onSubmit: (values: LinkAPIData, helpers: FormikHelpers<LinkAPIData>) => any
}

// Helpers ----

// Get a date/time as an ISO string without milliseconds
function timeString(time: DateTime): string {
  return time.startOf('second').toISO({suppressMilliseconds: true})
}

// Get the current date/time as an ISO string without milliseconds
function currentTimeString(): string {
  return timeString(DateTime.local())
}

// Prepare initial form values
function values(link?: Link | null): LinkAPIData {
  return {
    text: link?.text || '',
    url: link?.url || '',
    offline_url: link?.offline_url || '',
    options: link?.options ?? [],
    icon: link?.icon || IconName.LINK,
    category: link?.category || 'overview',
    published_at: link?.published_at ? timeString(link.published_at) : null,
    featured_at: link?.featured_at ? timeString(link.featured_at) : null,
    site_ids: link?.sites.map(site => site.id) ?? [],
  }
}

// Get all icon names as options for a select input
const iconOptions: Array<Option> = Object.values(IconName).map(icon => ({
  id: icon, name: toTitleCase(icon), icon: icon
}))

// Component ----

function LinkForm({link, onSubmit}: LinkFormProps) {
  const {termFor} = useOperator();
  const [published, setPublished] = useState<boolean>(!!link?.published_at);
  const [featured, setFeatured] = useState<boolean>(!!link?.featured_at);
  const linkOptions = useLinkOptions('link');

  function handlePublishToggle(isPublished: boolean, {setFieldValue}: FormikHelpers<any>) {
    setPublished(isPublished)

    if (isPublished) {
      void setFieldValue('published_at', currentTimeString())
    } else {
      setFeatured(false)
      void setFieldValue('published_at', null)
      void setFieldValue('featured_at', null)
    }
  }

  function handleFeatureToggle(isFeatured: boolean, {setFieldValue}: FormikHelpers<any>) {
    setFeatured(isFeatured)
    void setFieldValue('featured_at', isFeatured ? currentTimeString() : null)
  }

  function handleSubmit(values: LinkAPIData, helpers: FormikHelpers<LinkAPIData>) {
    return onSubmit(values, helpers)
  }

  const validationSchema = Yup.object().shape({
    text: Yup.string().required("This field is required").max(100, "Maximum character limit exceeded"),
    icon: Yup.string().required("This field is required"),
    url: Yup.string().required('This field is required').matches(linkUrlRegex, 'This field is required'),
  })

  return (
    <Form initialValues={values(link)} validationSchema={validationSchema} onSubmit={handleSubmit}>
      <FormSection
        name="Link Setup"
        description="Choose how this link appears, and where it goes"
        columns={6}
      >

        {/* Text */}
        <FormBlock columns={3}>
          <TextField
            name="text"
            label="Text *"
            icon={TagIcon}
            hint="100 chars max"
            help="The text shown in the link button"
            autoComplete="off"
            maxLength={100}
          />
        </FormBlock>

        {/* Icon */}
        <FormBlock columns={2}>
          <SelectField
            name="icon"
            label="Icon *"
            help="The icon shown next to the text in the link button"
            options={iconOptions}
          />
        </FormBlock>

        {/* URL */}
        <FormBlock columns={6}>
          <LinkUrlFieldSet label={'Link To'} name={'url'} offlineName={'offline_url'} optionsName={'options'} options={linkOptions}/>
        </FormBlock>

        {/* Category */}
        <FormBlock columns={2}>
          <SelectField
            name="category"
            label="Category"
            hint="Optional"
            help="This will dictate where the link is displayed"
            options={[
              {id: "overview", name: "Overview"},
              {id: "wallet-tickets", name: termFor('Tickets')},
              {id: "wallet-memberships", name: termFor('Memberships')},
              {id: "wallet-vouchers", name: termFor('Vouchers')},
            ]}
          />
        </FormBlock>
      </FormSection>

      <FormSection
        name={termFor('Sites')}
        description={`Choose which ${termFor('sites')} to associate this with.`}
        columns={6}
      >
        <FormBlock columns={6}>
          <SiteAttachmentFieldSet name={"site_ids"} selected={link?.sites}/>
        </FormBlock>
      </FormSection>

      <FormSection
        name="Publishing"
        description="Make this link available and/or highlighted in-app"
        columns={6}
      >

        {/* Published Toggle */}
        <FormBlock columns={3}>
          {form => (
            <ToggleSwitch
              label="Published"
              description="Mark this link as published, to display it to your audience"
              checked={published}
              onChange={enabled => handlePublishToggle(enabled, form)}
              multiline
            />
          )}
        </FormBlock>

        {/* Featured Toggle */}
        {published &&
          <FormBlock columns={3}>
            {form => (
              <ToggleSwitch
                label="Featured"
                description="Mark this link as featured, to display it prominently"
                checked={featured}
                onChange={enabled => handleFeatureToggle(enabled, form)}
                multiline
              />
            )}
          </FormBlock>
        }
      </FormSection>
    </Form>
  )
}

export default LinkForm;